import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FavoritesList } from '@evi/shared';
import {
  addFavorite, checkFavorite, fetchFavorites, removeFavorite,
} from '@/lib/api';

/**
 * React-Query hooks backing the driver favourites flow.
 *
 * Two read keys, one mutation:
 *   ['favorites']              — the list (FavoritesPage)
 *   ['favorites','check', id]  — per-station is-favorited (HeartButton)
 *   useToggleFavorite()        — optimistic flip + rollback + dual
 *                                invalidation so list + check stay
 *                                in sync after a round-trip.
 *
 * `staleTime: 60_000` — favourites don't change on the server between
 * page visits; a minute's freshness is plenty and avoids refetches on
 * every station detail remount.
 */

const FAVORITES_KEY = ['favorites'] as const;
const favoriteCheckKey = (stationId: string) =>
  ['favorites', 'check', stationId] as const;

export function useFavorites() {
  return useQuery<FavoritesList>({
    queryKey: FAVORITES_KEY,
    queryFn: fetchFavorites,
    staleTime: 60_000,
  });
}

export function useIsFavorite(stationId: string | undefined) {
  return useQuery<boolean>({
    queryKey: stationId
      ? favoriteCheckKey(stationId)
      : ['favorites', 'check', 'none'],
    queryFn: () => checkFavorite(stationId!),
    enabled: Boolean(stationId),
    staleTime: 60_000,
  });
}

interface ToggleArgs {
  stationId: string;
  /** Current favourited state — the mutation flips this. */
  favorited: boolean;
}

export function useToggleFavorite() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ stationId, favorited }: ToggleArgs) =>
      favorited ? removeFavorite(stationId) : addFavorite(stationId),

    // Optimistic flip on the specific check key so the heart fills
    // instantly. Cancel any in-flight refetch before mutating the
    // cache to avoid a stale write racing us back.
    onMutate: async ({ stationId, favorited }) => {
      await qc.cancelQueries({ queryKey: favoriteCheckKey(stationId) });
      const prev = qc.getQueryData<boolean>(favoriteCheckKey(stationId));
      qc.setQueryData(favoriteCheckKey(stationId), !favorited);
      return { prev, stationId };
    },

    // Roll back the optimistic write on failure so the heart reverts.
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      qc.setQueryData(favoriteCheckKey(ctx.stationId), ctx.prev);
    },

    // Regardless of success/failure, invalidate both the check and the
    // list so the server's view is fetched next time either is read.
    onSettled: (_data, _err, { stationId }) => {
      qc.invalidateQueries({ queryKey: favoriteCheckKey(stationId) });
      qc.invalidateQueries({ queryKey: FAVORITES_KEY });
    },
  });
}
